import React from 'react';
import { HiMinus } from 'react-icons/hi';
import { FiAlertTriangle } from 'react-icons/fi';
import './styles.css';
import { getCompletionStyles } from '../../utils/taskUtils';
import { HighlightedText } from '../../utils/highlightUtils';
import AllTasksDrawer from './AllTasksDrawer';
import TaskHoverTooltip from '../common/TaskHoverTooltip';
import TaskCardTooltipContent from '../../pages/Resource/components/Timeline/TaskCardTooltipContent';
import { DashboardStatus, dashboardStatusHexColors, getDashboardStatusLabel } from '../../constants/integrationTypes';

interface Task {
  item_id: string;
  project_name: string;
  task_title: string;
  duration: number;
  start: string;
  end: string;
  project_color: string;
  project_id?: string;
  completion?: number;
  due_date?: string | null;
  status?: string;
  status_label?: string;
  status_color?: string;
  progress?: number;
  integration_item_type?: string;
  stack_rank?: string | number | null;
  integration_team_name?: string;
  integration_project_name?: string;
  closed_outside_sprint?: boolean;
}

interface StackedCardsProps {
  currentTask: Task;
  overlappingTasks: Task[];
  onTaskClick: (task: Task) => void;
  onDeleteTask: (task: Task) => void;
  searchText?: string;
  onAllTasksOpen?: () => void;
  boxWidth?: number;
  resourceName?: string;
  isSingleTeamSelected?: boolean;
  isSprintFilter?: boolean;
}

const StackedCards: React.FC<StackedCardsProps> = ({
  currentTask,
  overlappingTasks,
  onTaskClick,
  onDeleteTask,
  searchText = "",
  onAllTasksOpen,
  boxWidth = 75,
  resourceName,
  isSingleTeamSelected = false,
  isSprintFilter = false
}) => {
  const [showMoreTasksModal, setShowMoreTasksModal] = React.useState(false);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string>(currentTask.item_id);

  const getStatusInfo = (task: Task) => {
    const statusKey = task.status?.toLowerCase?.() || '';
    const derivedLabel = statusKey ? getDashboardStatusLabel(statusKey) : undefined;
    const label = derivedLabel || 'Not set';
    const color = (statusKey ? dashboardStatusHexColors[statusKey] : undefined) || '#a3a3a3';
    return { label, color };
  };

  // Helper function to get stack_rank value for sorting
  const getStackRankValue = (stackRank?: string | number | null): number => {
    if (stackRank === null || stackRank === undefined || stackRank === '') return Number.MAX_SAFE_INTEGER;
    if (typeof stackRank === 'number') return stackRank;
    if (typeof stackRank === 'string') {
      const numRank = Number.parseFloat(stackRank);
      if (!Number.isNaN(numRank)) return numRank;
    }
    return Number.MAX_SAFE_INTEGER;
  };

  // Sort all tasks by duration in ascending order (or by stack_rank if single team selected)
  const sortedTasks = React.useMemo(() => {
    const allTasks = [currentTask, ...overlappingTasks];

    // When single team is selected, sort by stack_rank first
    if (isSingleTeamSelected) {
      return allTasks.sort((a, b) => {
        const stackRankA = getStackRankValue(a.stack_rank);
        const stackRankB = getStackRankValue(b.stack_rank);
        const stackRankDiff = stackRankA - stackRankB;
        if (stackRankDiff !== 0) return stackRankDiff;
        // Fall back to duration if stack_rank is the same
        return a.duration - b.duration;
      });
    }

    // Default sorting: by duration ascending
    return allTasks.sort((a, b) => a.duration - b.duration);
  }, [currentTask, overlappingTasks, isSingleTeamSelected]);

  const totalTasks = overlappingTasks.length;
  // Get first 3 tasks after sorting (including current task)
  const visibleTasks = sortedTasks.slice(1, 3); // Skip first one as it will be rendered separately
  const topTask = sortedTasks[0]; // Task with highest duration

  // Combine all tasks for the modal
  const allTasks = sortedTasks;

  // Add this helper function to get max duration
  const getMaxDuration = () => {
    return Math.max(...allTasks.map(task => task.duration));
  };

  // Get max duration once
  const maxDuration = getMaxDuration();

  const getStackTransform = (index: number) => `translateY(${index * 4}px)`;


  const renderTaskContent = (task: Task) => {
    const { label: statusLabel, color: statusColor } = getStatusInfo(task);
    const status = task?.status?.toLowerCase?.() || '';
    const showRiskTriangle = status === DashboardStatus.DELAYED || status === DashboardStatus.UNALLOCATED || status === DashboardStatus.BLOCKED;
    const showClosedOutsideIndicator = isSprintFilter && task.closed_outside_sprint;

    return (
      <TaskHoverTooltip
        placement="right"
        arrow
        title={
          <TaskCardTooltipContent
            task={task as any}
            statusLabel={statusLabel}
            statusColor={statusColor}
            isSprintFilter={isSprintFilter}
            isSingleTeamSelected={isSingleTeamSelected}
          />
        }
      >
        <div
          className="task-container group flex flex-wrap rounded-lg px-2 py-0 top-[2px] transition-all duration-200"
          style={{
            background: `linear-gradient(135deg, ${task.project_color}dd, ${task.project_color}99)`,
            '--solid-bg': `linear-gradient(135deg, ${task.project_color}, ${task.project_color})`,
            width: `${task.duration * boxWidth}px`,
            maxHeight: '60px',
            height: 'calc(60px - 4px)',
            boxSizing: 'border-box',
            overflow: 'visible',
            position: 'relative',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(8px)'
          } as React.CSSProperties}
        >
          <div
            className="task-overlay absolute inset-0 pointer-events-none opacity-40 transition-opacity duration-200 rounded-lg"
            style={{
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.2))'
            }}
          />

          <button
            className="hidden group-hover:flex absolute right-[-8px] top-[-8px] p-1.5 rounded-full transition-all duration-200 text-white bg-red-600/95 hover:bg-red-700 cursor-pointer backdrop-blur-sm border border-red-400/30 shadow-lg items-center justify-center hover:scale-110"
            style={{ zIndex: 9999 }}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              onDeleteTask(task);
            }}
            title="Remove work item"
          >
            <HiMinus className="w-3 h-3 text-white" />
          </button>

          <div
            className="whitespace-nowrap text-ellipsis overflow-hidden w-full text-white relative z-10"
            style={{
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
            }}
          >
            <span className="text-sm font-normal font-['Inter'] leading-[21px]">
              {showRiskTriangle && (
                <span title="This is contributing to risk" className="mr-1 inline-flex items-center flex-shrink-0">
                  <FiAlertTriangle className="text-red-500 w-3 h-3" style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }} />
                </span>
              )}
              {showClosedOutsideIndicator && (
                <span title="Closed outside sprint" className="mr-1 inline-flex items-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-cyan-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 14l2 2 4-4" />
                  </svg>
                </span>
              )}
              <HighlightedText
                text={task.task_title}
                searchTerm={searchText}
              /> (Work Item #{task.item_id}){" "}
            </span>
            <span className="text-sm font-semibold font-['Inter'] leading-[21px]">
              <HighlightedText
                text={task.project_name}
                searchTerm={searchText}
              />:{" "}
            </span>
          </div>
          <div className="flex justify-between items-end w-full mt-1 relative z-10">
            <span
              className="text-white/90 text-xs font-medium font-['Inter'] leading-[18px]"
              style={{
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              {task.duration}d
            </span>
            {task.completion !== undefined && (
              <div
                className="flex items-center px-1.5 py-0.5 rounded text-xs font-bold text-white [text-shadow:0_0_8px_rgba(0,0,0,0.3)]"
                style={getCompletionStyles(task.completion, statusColor)}
              >
                {task.completion}%
              </div>
            )}
          </div>
        </div>
      </TaskHoverTooltip>
    );
  };

  const renderMoreTasksCard = () => (
    <div
      className="card stacked-card cursor-pointer"
      style={{
        backgroundColor: '#1e40af',
        zIndex: totalTasks - 2,
        transform: getStackTransform(2),
        width: `${maxDuration * boxWidth}px`
      }}
      onClick={() => {
        setShowMoreTasksModal(true);
        if (onAllTasksOpen) onAllTasksOpen();
      }}
    >
      <div
        className="task-container flex flex-wrap rounded-lg p-2 items-center justify-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #2563ebdd, #1d4ed899)',
          '--solid-bg': 'linear-gradient(135deg, #2563eb, #1d4ed8)',
          position: 'relative',
          width: '100%',
          maxHeight: '60px',
          height: '60px',
          border: '1px solid rgba(255, 255, 255, 0.15)',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(8px)'
        } as React.CSSProperties}
      >
        <div
          className="task-overlay absolute inset-0 pointer-events-none opacity-40 transition-opacity duration-200 rounded-lg"
          style={{
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.1), rgba(0,0,0,0.2))'
          }}
        />
        <div className="text-white text-xs font-semibold font-['Inter'] relative z-10">
          View All ({totalTasks + 1} Work Items)
        </div>
      </div>
    </div>
  );

  const TaskCountBadge = ({ count }: { count: number }) => (
    count > 0 ? (
      <div className="task-count">
        +{count}
      </div>
    ) : null
  );

  if (!allTasks.length) return null;

  return (
    <>
      <div className="stacked-container" style={{ width: `${maxDuration * boxWidth}px` }}>
        {/* Highest duration task on top */}
        <div
          className="card current-card"
          style={{
            backgroundColor: topTask.project_color,
            zIndex: totalTasks + 2,
            width: `${topTask.duration * boxWidth}px`
          }}
          onClick={() => onTaskClick(topTask)}
        >
          {renderTaskContent(topTask)}
          <TaskCountBadge count={totalTasks} />
        </div>

        {/* Next two highest duration tasks */}
        {visibleTasks.map((task: Task, index: number) => (
          <div
            key={task.item_id}
            className="card stacked-card"
            style={{
              backgroundColor: task.project_color,
              zIndex: totalTasks - index,
              opacity: 5
            }}
            onClick={() => onTaskClick(task)}
          >
            {renderTaskContent(task)}
          </div>
        ))}

        {/* Show "View All" card if there is more than 1 task total */}
        {totalTasks > 0 && renderMoreTasksCard()}
      </div>

      {/* All Tasks Drawer via Portal */}
      <AllTasksDrawer
        isOpen={showMoreTasksModal}
        onClose={() => setShowMoreTasksModal(false)}
        tasks={allTasks}
        selectedTaskId={selectedTaskId}
        onSelectTask={(task) => {
          setSelectedTaskId(task.item_id);
          onTaskClick(task);
        }}
        onDeleteTask={onDeleteTask}
        searchText={searchText}
        resourceName={resourceName}
        isSingleTeamSelected={isSingleTeamSelected}
        isSprintFilter={isSprintFilter}
      />
    </>
  );
};

export default StackedCards; 